import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { Badge, Button, Input, Modal, Select, Spinner } from '@/components/ui';
import {
  createDraftVersion,
  DOCUMENT_TYPE_LABELS,
  getTemplate,
  getVersion,
  listVariables,
  previewVersion,
  returnVersionToDraft,
  runVersionCommand,
  saveDraft,
  type SignerRole,
  type TemplateBlock,
  type TemplateBlockKind,
  type TemplateSigner,
  type TemplateVersion,
  type VersionContent,
} from '@/api/services/hrDocuments.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { VERSION_STATUS } from './documentFormat';

const TEXTAREA_CLASS =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

const BLOCK_KINDS: Array<{ value: TemplateBlockKind; label: string }> = [
  { value: 'heading', label: 'Heading' },
  { value: 'paragraph', label: 'Paragraph' },
  { value: 'bullets', label: 'Bullet list' },
  { value: 'spacer', label: 'Space' },
  { value: 'signature', label: 'Signature line' },
];

const SIGNER_ROLES: Array<{ value: SignerRole; label: string }> = [
  { value: 'hr', label: 'HR (the person who issues it)' },
  { value: 'employee', label: 'The employee' },
  { value: 'manager', label: 'Reporting manager' },
];

function toContent(version: TemplateVersion): VersionContent {
  return {
    title: version.title,
    blocks: version.blocks.map((block) => ({ ...block })),
    signers: version.signers.map(({ order, role, userId, label }) => ({
      order,
      role,
      label,
      ...(userId && { userId }),
    })),
    requiresSignature: version.requiresSignature,
    expiresAfterDays: version.expiresAfterDays,
  };
}

/**
 * Edits one template version. Drafts are editable; a version awaiting
 * approval can be approved by someone other than its author or returned; an
 * approved version is read-only and can seed a new draft. The server enforces
 * every rule; the dialog only avoids offering what it will refuse.
 */
export function TemplateEditorDialog({
  companyId,
  templateId,
  onClose,
}: {
  companyId: string;
  templateId: string;
  onClose: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [versionId, setVersionId] = useState<string | null>(null);
  // Local edits and the preview belong to one loaded version; when the server
  // version changes (another version, or a save), both fall back to it.
  const [edits, setEdits] = useState<{ key: string; content: VersionContent } | null>(null);
  const [previewState, setPreviewState] = useState<{ key: string; html: string } | null>(null);
  const [returnReason, setReturnReason] = useState('');
  const [returning, setReturning] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const lastField = useRef<{ index: number; item?: number } | 'title' | null>(null);

  const { data: detail } = useQuery({
    queryKey: ['hr-document-template', companyId, templateId],
    queryFn: () => getTemplate(companyId, templateId),
  });
  const versions = detail?.versions ?? [];
  const selectedId =
    versionId ??
    versions.find((row) => row.status === 'draft' || row.status === 'pending_approval')?._id ??
    versions.find((row) => row.status === 'approved')?._id ??
    null;

  const { data: version, isLoading } = useQuery({
    queryKey: ['hr-document-version', companyId, selectedId],
    queryFn: () => getVersion(companyId, selectedId!),
    enabled: Boolean(selectedId),
  });
  const { data: variables = [] } = useQuery({
    queryKey: ['hr-document-variables', companyId, detail?.template.type],
    queryFn: () => listVariables(companyId, detail!.template.type),
    enabled: Boolean(detail),
    staleTime: Infinity,
  });

  const versionKey = version ? `${version._id}:${version.updatedAt ?? ''}:${version.status}` : null;
  const content: VersionContent | null = version
    ? edits?.key === versionKey
      ? edits.content
      : toContent(version)
    : null;
  const previewHtml = previewState?.key === versionKey ? previewState.html : null;

  const editable = version?.status === 'draft';
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['hr-document-template', companyId, templateId] });
    void queryClient.invalidateQueries({ queryKey: ['hr-document-version', companyId] });
    void queryClient.invalidateQueries({ queryKey: ['hr-document-templates', companyId] });
  };
  const fail = (error: unknown): void => setFormError(getErrorMessage(error));

  const save = useMutation({
    mutationFn: () => saveDraft(companyId, selectedId!, content!),
    onSuccess: () => {
      toast.success('Draft saved');
      refresh();
    },
    onError: fail,
  });
  const command = useMutation({
    mutationFn: async (name: 'submit' | 'approve' | 'discard') => {
      if (name === 'submit') await saveDraft(companyId, selectedId!, content!);
      await runVersionCommand(companyId, selectedId!, name);
      return name;
    },
    onSuccess: (name) => {
      toast.success(
        name === 'submit'
          ? 'Sent for approval'
          : name === 'approve'
            ? 'Approved. New documents use this version.'
            : 'Draft discarded'
      );
      if (name === 'discard') setVersionId(null);
      refresh();
    },
    onError: fail,
  });
  const giveBack = useMutation({
    mutationFn: () => returnVersionToDraft(companyId, selectedId!, returnReason.trim()),
    onSuccess: () => {
      toast.success('Returned to draft');
      setReturning(false);
      setReturnReason('');
      refresh();
    },
    onError: fail,
  });
  const newDraft = useMutation({
    mutationFn: () => createDraftVersion(companyId, templateId),
    onSuccess: (created) => {
      setVersionId(created._id);
      refresh();
    },
    onError: fail,
  });
  const preview = useMutation({
    mutationFn: async () => {
      if (editable) await saveDraft(companyId, selectedId!, content!);
      return previewVersion(companyId, selectedId!);
    },
    onSuccess: (html) => versionKey && setPreviewState({ key: versionKey, html }),
    onError: fail,
  });

  const update = (next: Partial<VersionContent>): void => {
    if (content && versionKey) setEdits({ key: versionKey, content: { ...content, ...next } });
  };
  const updateBlock = (index: number, next: Partial<TemplateBlock>): void =>
    update({
      blocks: content!.blocks.map((block, at) => (at === index ? { ...block, ...next } : block)),
    });
  const moveBlock = (index: number, delta: number): void => {
    const blocks = [...content!.blocks];
    const target = index + delta;
    if (target < 0 || target >= blocks.length) return;
    [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
    update({ blocks });
  };
  const updateSigner = (index: number, next: Partial<TemplateSigner>): void =>
    update({
      signers: content!.signers.map((signer, at) =>
        at === index ? { ...signer, ...next } : signer
      ),
    });

  /** Appends a placeholder to the text field last focused, else the title. */
  const insertVariable = (key: string): void => {
    if (!content || !editable) return;
    const token = `{{${key}}}`;
    const target = lastField.current;
    if (target && target !== 'title') {
      const block = content.blocks[target.index];
      if (block?.kind === 'bullets' && target.item !== undefined) {
        const items = [...(block.items ?? [])];
        items[target.item] = `${items[target.item] ?? ''} ${token}`.trim();
        updateBlock(target.index, { items });
        return;
      }
      if (block && block.kind !== 'spacer' && block.kind !== 'signature') {
        updateBlock(target.index, { text: `${block.text ?? ''} ${token}`.trim() });
        return;
      }
    }
    update({ title: `${content.title} ${token}`.trim() });
  };

  const signerOptions = (content?.signers ?? []).map((signer) => ({
    value: String(signer.order),
    label: `Signer ${signer.order}`,
  }));

  let body: React.ReactNode;
  if (!detail || isLoading || !content || !version) {
    body = (
      <div className="flex justify-center py-10" role="status">
        <Spinner />
        <span className="sr-only">Loading template</span>
      </div>
    );
  } else {
    const status = VERSION_STATUS[version.status];
    body = (
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={status.variant}>{status.label}</Badge>
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {DOCUMENT_TYPE_LABELS[detail.template.type]} · version {version.version}
          </span>
          {versions.length > 1 && (
            <div className="ml-auto w-44">
              <Select
                label="Version"
                value={selectedId ?? ''}
                onChange={(value) => setVersionId(value)}
                options={versions.map((row) => ({
                  value: row._id,
                  label: `v${row.version} · ${VERSION_STATUS[row.status].label}`,
                }))}
              />
            </div>
          )}
        </div>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_240px]">
          <div className="space-y-4">
            <Input
              id="template-title"
              label="Document title"
              value={content.title}
              disabled={!editable}
              onFocus={() => (lastField.current = 'title')}
              onChange={(event) => update({ title: event.target.value })}
            />

            <fieldset className="space-y-3">
              <legend className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                Content
              </legend>
              {content.blocks.map((block, index) => (
                <div
                  key={index}
                  className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700"
                >
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="w-40">
                      <Select
                        label={`Block ${index + 1}`}
                        value={block.kind}
                        disabled={!editable}
                        onChange={(value) =>
                          updateBlock(index, {
                            kind: value as TemplateBlockKind,
                            ...(value === 'signature' && { signerOrder: 1 }),
                            ...(value === 'bullets' && !block.items && { items: [''] }),
                          })
                        }
                        options={BLOCK_KINDS}
                      />
                    </div>
                    {block.kind === 'signature' && (
                      <div className="w-32">
                        <Select
                          label="Signer"
                          value={String(block.signerOrder ?? 1)}
                          disabled={!editable}
                          onChange={(value) => updateBlock(index, { signerOrder: Number(value) })}
                          options={signerOptions}
                        />
                      </div>
                    )}
                    {editable && (
                      <div className="ml-auto flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Move block ${index + 1} up`}
                          disabled={index === 0}
                          onClick={() => moveBlock(index, -1)}
                        >
                          <ArrowUp className="h-4 w-4" aria-hidden />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Move block ${index + 1} down`}
                          disabled={index === content.blocks.length - 1}
                          onClick={() => moveBlock(index, 1)}
                        >
                          <ArrowDown className="h-4 w-4" aria-hidden />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Remove block ${index + 1}`}
                          onClick={() =>
                            update({ blocks: content.blocks.filter((_, at) => at !== index) })
                          }
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </Button>
                      </div>
                    )}
                  </div>
                  {(block.kind === 'heading' || block.kind === 'paragraph') && (
                    <textarea
                      aria-label={`Block ${index + 1} text`}
                      rows={block.kind === 'heading' ? 1 : 3}
                      value={block.text ?? ''}
                      disabled={!editable}
                      onFocus={() => (lastField.current = { index })}
                      onChange={(event) => updateBlock(index, { text: event.target.value })}
                      className={TEXTAREA_CLASS}
                    />
                  )}
                  {block.kind === 'bullets' && (
                    <div className="space-y-2">
                      {(block.items ?? []).map((item, itemIndex) => (
                        <div key={itemIndex} className="flex gap-2">
                          <input
                            aria-label={`Block ${index + 1} item ${itemIndex + 1}`}
                            value={item}
                            disabled={!editable}
                            onFocus={() => (lastField.current = { index, item: itemIndex })}
                            onChange={(event) => {
                              const items = [...(block.items ?? [])];
                              items[itemIndex] = event.target.value;
                              updateBlock(index, { items });
                            }}
                            className={TEXTAREA_CLASS}
                          />
                          {editable && (
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Remove item ${itemIndex + 1}`}
                              onClick={() =>
                                updateBlock(index, {
                                  items: (block.items ?? []).filter((_, at) => at !== itemIndex),
                                })
                              }
                            >
                              <Trash2 className="h-4 w-4" aria-hidden />
                            </Button>
                          )}
                        </div>
                      ))}
                      {editable && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            updateBlock(index, { items: [...(block.items ?? []), ''] })
                          }
                        >
                          <Plus className="mr-1 h-4 w-4" aria-hidden />
                          Add item
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {editable && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    update({ blocks: [...content.blocks, { kind: 'paragraph', text: '' }] })
                  }
                >
                  <Plus className="mr-1 h-4 w-4" aria-hidden />
                  Add block
                </Button>
              )}
            </fieldset>

            <fieldset className="space-y-3">
              <legend className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                Signing
              </legend>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  checked={content.requiresSignature}
                  disabled={!editable}
                  onChange={(event) => update({ requiresSignature: event.target.checked })}
                />
                Requires signatures
              </label>
              {content.signers.map((signer, index) => (
                <div key={index} className="flex flex-wrap items-end gap-2">
                  <span className="pb-2 text-sm text-gray-600 dark:text-gray-300">
                    {signer.order}.
                  </span>
                  <div className="w-56">
                    <Select
                      label="Who signs"
                      value={signer.role}
                      disabled={!editable}
                      onChange={(value) => updateSigner(index, { role: value as SignerRole })}
                      options={SIGNER_ROLES}
                    />
                  </div>
                  <div className="min-w-40 flex-1">
                    <Input
                      id={`signer-label-${index}`}
                      label="Caption under the signature"
                      value={signer.label}
                      disabled={!editable}
                      onChange={(event) => updateSigner(index, { label: event.target.value })}
                    />
                  </div>
                  {editable && (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove signer ${signer.order}`}
                      onClick={() =>
                        update({
                          signers: content.signers
                            .filter((_, at) => at !== index)
                            .map((row, at) => ({ ...row, order: at + 1 })),
                        })
                      }
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  )}
                </div>
              ))}
              {editable && content.signers.length < 5 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    update({
                      signers: [
                        ...content.signers,
                        { order: content.signers.length + 1, role: 'employee', label: 'Signature' },
                      ],
                    })
                  }
                >
                  <Plus className="mr-1 h-4 w-4" aria-hidden />
                  Add signer
                </Button>
              )}
              <div className="w-48">
                <Input
                  id="template-expiry"
                  type="number"
                  label="Days to sign"
                  min={1}
                  max={90}
                  value={String(content.expiresAfterDays)}
                  disabled={!editable}
                  onChange={(event) =>
                    update({ expiresAfterDays: Math.max(1, Number(event.target.value) || 1) })
                  }
                />
              </div>
            </fieldset>
          </div>

          <aside aria-labelledby="variables-heading" className="space-y-2">
            <h3
              id="variables-heading"
              className="text-sm font-semibold text-gray-900 dark:text-gray-100"
            >
              Variables
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-300">
              {editable
                ? 'Click to add to the field you last edited. Values come from HR records when a document is generated.'
                : 'Values come from HR records when a document is generated.'}
            </p>
            <ul className="space-y-1">
              {variables.map((variable) => (
                <li key={variable.key}>
                  <button
                    type="button"
                    disabled={!editable}
                    onClick={() => insertVariable(variable.key)}
                    className="w-full rounded-md px-2 py-1 text-left text-xs text-gray-700 hover:bg-gray-100 disabled:cursor-default disabled:hover:bg-transparent dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    {variable.label}
                    {variable.compensation && (
                      <span className="ml-1 text-amber-700 dark:text-amber-400">· pay</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </aside>
        </div>

        {previewHtml && (
          <section aria-label="Preview">
            <iframe
              title="Template preview"
              srcDoc={previewHtml}
              sandbox=""
              className="h-[60vh] w-full rounded-lg border border-gray-200 bg-white dark:border-gray-700"
            />
          </section>
        )}

        {returning && (
          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (returnReason.trim().length >= 3) giveBack.mutate();
            }}
          >
            <Input
              id="return-reason"
              label="What needs to change?"
              value={returnReason}
              onChange={(event) => setReturnReason(event.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setReturning(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={returnReason.trim().length < 3}
                loading={giveBack.isPending}
              >
                Return to draft
              </Button>
            </div>
          </form>
        )}

        {formError && (
          <p className="text-sm text-danger" role="alert">
            {formError}
          </p>
        )}
      </div>
    );
  }

  const busy =
    save.isPending || command.isPending || preview.isPending || newDraft.isPending;
  const footer = version ? (
    <div className="flex w-full flex-wrap items-center justify-between gap-2">
      <Button variant="ghost" size="sm" onClick={() => preview.mutate()} loading={preview.isPending}>
        Preview
      </Button>
      <div className="flex flex-wrap gap-2">
        {version.status === 'draft' && (
          <>
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => command.mutate('discard')}
            >
              Discard draft
            </Button>
            <Button variant="secondary" size="sm" loading={save.isPending} disabled={busy} onClick={() => save.mutate()}>
              Save draft
            </Button>
            <Button size="sm" disabled={busy} onClick={() => command.mutate('submit')}>
              Send for approval
            </Button>
          </>
        )}
        {version.status === 'pending_approval' && !returning && (
          <>
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => setReturning(true)}>
              Return to draft
            </Button>
            <Button size="sm" loading={command.isPending} disabled={busy} onClick={() => command.mutate('approve')}>
              Approve
            </Button>
          </>
        )}
        {(version.status === 'approved' || version.status === 'retired') &&
          !versions.some((row) => row.status === 'draft' || row.status === 'pending_approval') && (
            <Button size="sm" loading={newDraft.isPending} disabled={busy} onClick={() => newDraft.mutate()}>
              Start a new version
            </Button>
          )}
      </div>
    </div>
  ) : null;

  return (
    <Modal
      open
      onClose={onClose}
      title={detail?.template.name ?? 'Template'}
      className="max-w-5xl"
      footer={footer}
    >
      {body}
    </Modal>
  );
}
